import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import {
  Container,
  Button,
  Modal,
  Row,
  Col,
  Form,
  OverlayTrigger,
} from "react-bootstrap";

import { MdFilterList, MdEdit, MdDelete } from "react-icons/md";
import { Tooltip } from "@mui/material";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import loading from "../../assets/images/common/loading.gif";
import AppDataGrid from "../../components/common/AppDataGrid";


const Grades = () => {
  const [showFilterModal, setShowFilterModal] = useState(false);
  const [grades, setGrades] = useState([]);
  const [boards, setBoards] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [form, setForm] = useState({});

  const userData = sessionStorage.getItem("user");
  const userObj = userData ? JSON.parse(userData) : {};
  const readOnlyRoles = ["Class Teacher", "Teacher", "Class Incharge", "School Admin"];
  const canSubmit = !readOnlyRoles.includes(userObj.role_name?.trim());
  const baseUrl = process.env.REACT_APP_API_BASE_URL;
  const navigate = useNavigate();

  // Fetch grades
  const fetchGrades = async () => {
    try {
      const response = await axios.post(baseUrl + "/grades/", {
        action: "READ",
        school_id: userObj.school_id,
        board_id: userObj.board_id,
      });
      setGrades(response.data);
    } catch (error) {
      console.error("Error fetching grades:", error);
    } finally {
      setIsLoading(false);
    }
  };

  // Fetch boards
  const fetchBoards = async () => {
    try {
      const response = await axios.post(baseUrl + "/board/", { action: "READ" });
      setBoards(response.data);
    } catch (error) {
      console.error("Error fetching boards:", error);
    }
  };

  // Fetch subjects
  const fetchSubjects = async () => {
    try {
      const response = await axios.post(baseUrl + "/subjectmaster/", {
        action: "READ",
        school_id: userObj.school_id,
      });
      setSubjects(response?.data || []);
    } catch (error) {
      console.error("Error fetching subjects:", error);
    }
  };

  useEffect(() => {
    fetchGrades();
    fetchBoards();
    fetchSubjects();
  }, []);

  // Edit grade
  const handleEditClick = (grade_id) => {
    const gradeToEdit = grades.find((grade) => grade.grade_id === grade_id);
    if (gradeToEdit) {
      navigate("/addgrades", { state: { userData: gradeToEdit } });
    }
  };

  // Delete grade
  const handleDeleteClick = async (grade_id) => {
    const confirmDelete = window.confirm("Are you sure you want to update the status?");
    if (!confirmDelete) return;

    try {
      await axios.post(baseUrl + "/grades/", {
        grade_id,
        action: "DELETE",
      });
      toast.success("Record Set to Inactive");
      fetchGrades();
    } catch (error) {
      console.error("Error deleting grade:", error);
    }
  };

  // Search filter
  // Search filter
const filteredRecords = Array.isArray(grades)
  ? grades.filter((item) => {
      const query = searchQuery.toLowerCase();
      return (
        item.grade_name?.toLowerCase().includes(query) ||
        item.subject_name?.toLowerCase().includes(query) ||
        item.board_name?.toLowerCase().includes(query)
      );
    })
  : [];


  // MUI DataGrid columns
  const columns = [
    {
      field: "grade_name",
      headerName: "Grade Name",
      flex: 1,
      headerAlign: 'center',
      align: "center",
      renderCell: (params) => (
        <Tooltip title={params.value}>
          <span className="cell-wrap">{params.value}</span>
        </Tooltip>
      ),
    },
    {
      field: "min_marks",
      headerName: "Minimum Marks",
      flex: 1,
      headerAlign: 'center',
      align: "center",
      cellClassName: (params) =>
        /^\d+(\.\d+)?$/.test(params.value) ? "numeric-cell" : "",
    },
    {
      field: "max_marks",
      headerName: "Maximum Marks",
      flex: 1,
      headerAlign: 'center',
      align: "center",
      cellClassName: (params) =>
        /^\d+(\.\d+)?$/.test(params.value) ? "numeric-cell" : "",
    },
    {
      field: "grade_points",
      headerName: "Grade Points",
      flex: 1,
      headerAlign: 'center',
      align: "center",
      cellClassName: (params) =>
        /^\d+(\.\d+)?$/.test(params.value) ? "numeric-cell" : "",
    },
    {
      field: "subject_name",
      headerName: "Subject",
      flex: 1,
      headerAlign: 'center',
      align: "center",
    },
    {
      field: "board_name",
      headerName: "Curriculum",
      flex: 1,
      headerAlign: 'center',
      align: "center",
    },
    {
      field: "is_active",
      headerName: "Status",
      flex: 1,
      headerAlign: 'center',
      align: "center",
    },
    ...(canSubmit
      ? [
        {
          field: "actions",
          headerName: "Actions",
          flex: 1,
          sortable: false,
          headerAlign: 'center',
          align: "center",
          renderCell: (params) => (
            <div className="tableActions">
              <Tooltip title="Edit">
                <span
                  className="commonActionIcons"
                  onClick={() => handleEditClick(params.row.grade_id)}
                >
                  <MdEdit />
                </span>
              </Tooltip>
              <Tooltip title="Delete">
                <span
                  className="commonActionIcons"
                  onClick={() => handleDeleteClick(params.row.grade_id)}
                >
                  <MdDelete />
                </span>
              </Tooltip>
            </div>
          ),
        },
      ]
      : []),
  ];

  return (
    <div className="pageBody">
      <ToastContainer />

      {/* Header */}
      <div className="commonDataTableHead">
        <div className="d-flex justify-content-between align-items-center w-100">
          <h6 className="commonTableTitle">Grades</h6>
          <input
            type="text"
            placeholder="Search..."
            value={searchQuery}
            className="searchInput"
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          <OverlayTrigger
            placement="top"
            overlay={<Tooltip id="tooltip-top">Filter</Tooltip>}
          >
            <Button
              className="secondaryBtn"
              variant="secondary"
              onClick={() => setShowFilterModal(true)}
            >
              <MdFilterList />
            </Button>
          </OverlayTrigger>
        </div>
      </div>

      {/* Table */}
      <div className="commonTable">
        <div className="tableBody">
          {isLoading ? (
            <div className="loadingContainer">
              <img src={loading} alt="Loading..." className="loadingGif" />
            </div>
          ) : (
             <AppDataGrid
                        rows={filteredRecords}
                        columns={columns}
                        getRowId={(r) => r.grade_id}
                        showToolbar={false}
                        pagination
                        pageSizeOptions={[10, 20, 50]}
                        density="compact"
                        maxHeight={550}  
                    />
          )}
        </div>
      </div>

      {/* Filter Modal */}
      <Modal
        show={showFilterModal}
        onHide={() => setShowFilterModal(false)}
        className="commonFilterModal"
      >
        <Modal.Header closeButton className="modalHeaderFixed">
          <Modal.Title>Filter</Modal.Title>
        </Modal.Header>
        <Modal.Body className="modalBodyScrollable">
          <Row>
            <Col xs={12}>
              <div className="commonInput">
                <Form.Group controlId="grade_name">
                  <Form.Label>Grade Name</Form.Label>
                  <Form.Control
                    type="text"
                    value={form.grade_name || ""}
                    placeholder="Enter Grade Name"
                    onChange={(e) =>
                      setForm({ ...form, grade_name: e.target.value })
                    }
                  />
                </Form.Group>
              </div>
            </Col>

            <Col xs={12}>
              <div className="commonInput">
                <Form.Group controlId="min_marks">
                  <Form.Label>Minimum Marks</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.min_marks || ""}
                    placeholder="Enter Minimum Marks"
                    onChange={(e) =>
                      setForm({ ...form, min_marks: e.target.value })
                    }
                  />
                </Form.Group>
              </div>
            </Col>

            <Col xs={12}>
              <div className="commonInput">
                <Form.Group controlId="max_marks">
                  <Form.Label>Maximum Marks</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.max_marks || ""}
                    placeholder="Enter Maximum Marks"
                    onChange={(e) =>
                      setForm({ ...form, max_marks: e.target.value })
                    }
                  />
                </Form.Group>
              </div>
            </Col>

            <Col xs={12}>
              <div className="commonInput">
                <Form.Group controlId="grade_points">
                  <Form.Label>Grade Points</Form.Label>
                  <Form.Control
                    type="number"
                    value={form.grade_points || ""}
                    placeholder="Enter Grade Points"
                    onChange={(e) =>
                      setForm({ ...form, grade_points: e.target.value })
                    }
                  />
                </Form.Group>
              </div>
            </Col>

            <Col xs={12}>
              <div className="commonInput">
                <Form.Group>
                  <Form.Label>Subject</Form.Label>
                  <Form.Select
                    id="subject_id"
                    value={form.subject_id || ""}
                    onChange={(e) =>
                      setForm({ ...form, subject_id: e.target.value })
                    }
                  >
                    <option value="">Select Subject</option>
                    {(subjects||[])
                      .filter((subject) => subject.is_active === "Active")
                      .map((subject) => (
                        <option key={subject.subject_id} value={subject.subject_id}>
                          {subject.subject_name}
                        </option>
                      ))}
                  </Form.Select>
                </Form.Group>
              </div>
            </Col>

            <Col xs={12}>
              <div className="commonInput">
                <Form.Label>Curriculum</Form.Label>
                <Form.Select
                  id="board_id"
                  value={form.board_id || ""}
                  onChange={(e) =>
                    setForm({ ...form, board_id: e.target.value })
                  }
                >
                  <option value="">Select Board</option>
                  {boards.map((board) => (
                    <option key={board.board_id} value={board.board_id}>
                      {board.board_name}
                    </option>
                  ))}
                </Form.Select>
              </div>
            </Col>
          </Row>
        </Modal.Body>
        <Modal.Footer className="modalFooterFixed">
          <Button
            variant="secondary"
            className="btn-info clearBtn me-2"
            onClick={() => setForm({})}
          >
            Reset
          </Button>
          <Button
            variant="secondary"
            className="btn-danger secondaryBtn me-2"
            onClick={() => setShowFilterModal(false)}
          >
            Cancel
          </Button>
          <Button
            variant="primary"
            className="btn-success primaryBtn"
            onClick={fetchGrades}
          >
            Search
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Grades;
